import { Button } from '@fountain/fountain-ui-components';
import { CircularProgress, Typography } from '@material-ui/core';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import globalMessages from 'shared/global/messages';

import messages from './messages';
import { useStyles } from './styles';

interface BulkDecisionBarProps {
  count: number;
  running: boolean;
  onApprove: () => void;
  onReject: () => void;
  onClear: () => void;
}

/**
 * Approve or reject every selected opening at once (Design Requirements §5).
 *
 * ## In the flow, not floating over it
 *
 * Between the toolbar and the table rather than pinned to the viewport. A floating
 * bar covers the last row of a 25-row page, which is a row the reader may be about
 * to tick — and `layout-composition` keeps a contextual action in the region it
 * acts on. The bar appears only while something is selected, so it costs nothing
 * when it is not in use.
 *
 * ## It says the count, and the count is the whole point
 *
 * The reader is about to decide several things at once, so the number is the
 * heading rather than a detail beside the buttons. Announced with `role="status"`:
 * ticking a checkbox produces no other feedback that the count moved.
 *
 * Reject keeps the negative secondary weight it has on a single row —
 * `constraint-anti-pattern-guard` prohibits a destructive action in the primary
 * position, and this one is a rejection multiplied.
 */
export const BulkDecisionBar: React.FC<BulkDecisionBarProps> = ({
  count,
  running,
  onApprove,
  onReject,
  onClear,
}) => {
  const styles = useStyles();
  const intl = useIntl();

  return (
    <div className={styles.bulkBar}>
      <Typography className={styles.bulkCount} role="status" variant="body1">
        <FormattedMessage {...messages.bulkSelected} values={{ count }} />
      </Typography>

      <div className={styles.bulkActions}>
        {/* Leaves the rows alone, so it is neither of the two decisions and does
            not sit with them. */}
        <Button
          autoWidth
          size="small"
          type="text"
          disabled={running}
          onClick={onClear}
        >
          <FormattedMessage {...messages.bulkClear} />
        </Button>
        <Button
          autoWidth
          negative
          size="small"
          type="secondary"
          disabled={running}
          onClick={onReject}
        >
          <FormattedMessage {...globalMessages.reject} />
        </Button>
        <Button
          autoWidth
          size="small"
          type="primary"
          // Named explicitly because the label is swapped for a spinner while the
          // run is in flight, which would otherwise leave the control with no
          // accessible name at the moment somebody is waiting to hear what
          // happened. Same reason the row's own Approve carries one.
          aria-label={intl.formatMessage(globalMessages.approve)}
          disabled={running}
          onClick={onApprove}
        >
          {running ? (
            <CircularProgress size={16} />
          ) : (
            <FormattedMessage {...globalMessages.approve} />
          )}
        </Button>
      </div>
    </div>
  );
};
